import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IHotelInfo, ISessionKey, ITrustYouReview } from 'src/common-types/index';
import {
  SESSION_SEARCH_EXPIRED_MESSAGE,
  SESSION_EXPIRED_STATUS,
  Urls,
  Routes,
  SERVER_ERROR_STATUSES,
} from 'src/constants/index';
import { getHeaders, axiosInstance } from 'src/utils/index';
import { updateRooms, getRoomsDetails } from './rooms-search';
import { trustYouReviewActions } from './trust-you-review';
import { AppThunk } from '../index';

export interface IHotelDetailsState {
  hotel: IHotelInfo;
  loading: boolean;
  isServerError: boolean;
  error: string;
}

const initialState: IHotelDetailsState = {
  hotel: null,
  loading: true,
  error: '',
  isServerError: false,
};

const zero = 0;

const hotelDetailsSlice = createSlice({
  name: 'hotel-details',
  initialState,
  reducers: {
    setLoadingT: (state: IHotelDetailsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setErrorT: (state: IHotelDetailsState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setHotelDetails: (state: IHotelDetailsState, { payload }: PayloadAction<IHotelInfo>) => {
      state.hotel = payload;
    },
    setIsServerError: (state: IHotelDetailsState, { payload }: PayloadAction<boolean>) => {
      state.isServerError = payload;
    },
  },
});

export const { setLoadingT, setErrorT, setHotelDetails } = hotelDetailsSlice.actions;

export const hotelDetailsReducer = hotelDetailsSlice.reducer;

export const getHotelDetails = (
  hotelId: number,
  sessionKey: ISessionKey,
  isFromAdminPage: boolean,
): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(setLoadingT(true));
    dispatch(hotelDetailsSlice.actions.setIsServerError(false));

    const setRoomsExpireDate = (expireDate: string): void => {
      // @ts-ignore
      updateRooms(expireDate, dispatch, getState);
    };

    try {
      let res;

      if (isFromAdminPage) {
        const response = await axiosInstance.get(`${Urls.GetHotels}/${hotelId}`, {
          ...getHeaders(),
        });

        res = {
          data: {
            hotelDetails: response.data.hotelDetails,
            searchType: '',
            sessionKey: null,
          },
        };
      } else {
        res = await axiosInstance.post(
          Urls.HotelDetails,
          { hotelId, sessionKey },
          { ...getHeaders() },
        );

        setRoomsExpireDate(sessionKey.expireDate);
      }

      dispatch(setLoadingT(false));
      dispatch(setHotelDetails(res.data));
      dispatch(
        trustYouReviewActions.setReviewModule(
          res.data?.trustYouData?.metaReview?.response as ITrustYouReview,
        ),
      );

      if (res.data?.sessionKey?.isInvalid) {
        dispatch(getRoomsDetails(hotelId));
      }
    } catch (error) {
      console.error(error);
      dispatch(setErrorT(error.toString()));
      dispatch(setLoadingT(false));

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        error?.response?.data[zero] === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        history.replaceState(null, null, `${Routes.Search}${location.search}`);
        location.reload();
      } else if (SERVER_ERROR_STATUSES.includes(error?.response?.status)) {
        dispatch(hotelDetailsSlice.actions.setIsServerError(true));
      }
    }
  };
};

